// AddAppointments.js
import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Grid,
  Avatar,
  Stack,
  Typography,
  CircularProgress,
  Dialog,
  Chip,
  Button,
  Divider,
} from "@mui/material";
import ArrowCircleLeftOutlinedIcon from "@mui/icons-material/ArrowCircleLeftOutlined";
import { useNavigate, useLocation } from "react-router-dom";
import TimezoneSelect from "react-timezone-select";
import axios from "axios";
import moment from "moment-timezone";
import styled from "styled-components";

// import BookingModule from "./BookingComponents/TimePickerModule";
// import BookingContinue from "../addAppointment2/BookingContinue2";
// import AuthModal from "components/common/AuthModal/AuthModal";
import ScrollToTop from "components/common/ScrollToTop/ScrollToTop";
import { SEOHelmet } from "SEO/SEOHelmet.jsx";

import { getToken, getUser, getUserId } from "../../../auth/authenticate";
import useDeedToast from "hook/useDeedToast";
import useUser from "context/user/useUser";
import { UserType } from "config/constants/common";
import BookingModel from "pages/bookingModel/BookingModel";
import TimePickerModule from "./BookingComponents/TimePickerModule";

// 🌟 Styled Components
const PageWrapper = styled(Box)`
  background: linear-gradient(135deg, #f3f4f6 0%, #e0f2fe 100%);
  min-height: 100vh;
  padding: 32px 16px;
`;

const ContentWrapper = styled(Box)`
  max-width: 1000px;
  margin: 0 auto;
`;

const ProfileCard = styled(Box)`
  padding: 24px;
  border-radius: 20px;
  background: linear-gradient(145deg, #022b9cbb, #276cffff);
  color: white;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  margin-bottom: 24px;
`;

const SectionCard = styled(Box)`
  background: white;
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 24px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
`;

const FeeTag = styled(Typography)`
  color: white;
  padding: 8px 16px;
  border-radius: 12px;
  font-weight: 800;
  font-size: 25px;
  display: inline-block;
`;

const AddAppointments = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const path = location.pathname.split("/")[2];

  // const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true); // initial page load
  const [dateLoading, setDateLoading] = useState(false); // per-date fetch
  const [doctorData, setDoctorData] = useState(null);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [bookingModalOpen, setBookingModalOpen] = useState(false);
  const [bookingContinueOpen, setBookingContinueOpen] = useState(false);
  const [selectedUserTimeZone, setSelectedUserTimeZone] = useState(
    localStorage.getItem("Time-Zone") || moment.tz.guess()
  );
  // const [aptSelectedDate, setAptSelectedDate] = useState("");
  // const [selectTime, setSelectTime] = useState(null);

  const [selectedAppointmentDetails, setSelectedAppointmentDetails] =
    useState(null);

  // authentication status
  const [isUserAuthenticated, setIsUserAuthenticated] = useState(false);


  // doctorDetails={doctorData}
  // selectedTimeDetails={{
  // date: aptSelectedDate,
  // time: selectTime,
  // timeZone: selectedUserTimeZone
  // }}

  const userId = getUser()?.userId;
  const userInfo = getUser();
  const [token] = useState(getToken());
  const { settings, userRole } = useUser();
  const { deedToast } = useDeedToast();

  // Modal states

  // fetch doctor details
  useEffect(() => {
    fetchDoctorDetails(selectedUserTimeZone);
  }, [selectedUserTimeZone]);

  //  catch data from BookingModule
  const handleSlotSelection = useCallback((details) => {
    // 'details' is the clean object containing utcTime, local displays, etc.
    setSelectedAppointmentDetails(details);
  }, []);

  const handleAuthenticationSuccess = useCallback(() => {
    // 1. Update local state (optional, but good for local re-renders)
    setIsUserAuthenticated(true);

    // 2. CRITICAL: Trigger a check/update in the global user context/hook
    // This usually involves refetching user data or checking the token in local storage.
    // Assuming useUser() has a mechanism to refresh/update its status based on the new token:
    // window.location.reload();

    deedToast("Successfully Logged In! Refreshing session...", "success");
  }, [deedToast]);

  const fetchDoctorDetails = async (tz) => {
    if (!userId) return;
    // setLoading(true);
    setInitialLoading(true);
    try {
      const config = {
        headers: { Authorization: `Bearer ${token}` },
        params: { patientTimeZone: tz },
      };
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}user/doctor/${path}`,
        config
      );
      setDoctorData(response?.data?.data);
    } catch (err) {
      console.error("Failed to fetch doctor details:", err);
    } finally {
      // setLoading(false);
      setInitialLoading(false);
    }
  };

  const fetchDoctorDetailsByDate = async (date, tz) => {
    if (!userId) return;
    // setLoading(true);
    setDateLoading(true);
    try {
      const config = {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          date: date, // pass the selected date
          patientTimeZone: tz || selectedUserTimeZone,
        },
      };
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}user/doctor/bydate/${path}`,
        config
      );

      setDoctorData(response?.data?.data);
    } catch (err) {
      console.error("Failed to fetch doctor details by date:", err);
    } finally {
      // setLoading(false);
      setDateLoading(false);
    }
  };

  const handleUsersTimeZone = async (newTz) => {
    if (!newTz?.value) return;
    const tz = newTz.value;
    localStorage.setItem("Time-Zone", tz);
    setSelectedUserTimeZone(tz);

    if (!userId || userId === "GUEST") return;
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}user/timezone/update`,
        { userId: getUserId(), timeZone: tz }
      );
      deedToast("Timezone Updated Successfully!", "success");
      fetchDoctorDetails(tz);
    } catch {
      deedToast("Failed to update timezone.", "danger");
    }
  };

  const handleBooking = (date, slot) => {
    if (!selectedAppointmentDetails) return;

    // console.log("Final Booking Details:", selectedAppointmentDetails);

    setBookingModalOpen(true);

    if (userRole === UserType.GUEST) {
      setAuthModalOpen(true);
      // setBookingModalOpen(true);
    } else if (userRole === UserType.PATIENT) setBookingContinueOpen(true);
  };

  // Handle booking completion
  const handleBookingComplete = () => {
    alert(
      "Appointment booked successfully! You will receive a confirmation shortly."
    );
  };

  // Helper function to ensure absolute URL for images
  const getAbsoluteImageUrl = (imagePath) => {
    if (!imagePath) return null;

    // If already absolute URL, return as is
    if (imagePath.startsWith("http://") || imagePath.startsWith("https://")) {
      return imagePath;
    }

    // If relative path, make it absolute
    if (imagePath.startsWith("/")) {
      return `${
        process.env.REACT_APP_BACKEND_URL || "https://deedmed.lk"
      }${imagePath}`;
    }

    // If no leading slash, add it
    return `${
      process.env.REACT_APP_BACKEND_URL || "https://deedmed.lk"
    }/${imagePath}`;
  };

// One source of truth for how this professional is named and described.
  // Never assume "Dr." — the serializer already composed the right title.
  const displayName =
    doctorData?.displayName ||
    [doctorData?.prefix, doctorData?.name].filter(Boolean).join(" ") ||
    "";
  const professionLabel = doctorData?.professionType || "Health professional";
  const isDoctor = doctorData?.professionType === "Doctor";

  // SEO Data Generation
  const generateSEOData = () => {
    if (!doctorData) return {};

    const doctorName = doctorData?.name || "Doctor";
    const specialties = doctorData?.serviceCategories?.join(", ") || "";
    const fee = doctorData?.displayingFee || "";
    const experience = doctorData?.experienceYears || "";
    const qualifications = doctorData?.qualifications?.join(", ") || "";

    // Fix: Properly handle OG image with absolute URL and fallback
    const doctorImagePath = doctorData?.User?.profileImagePath;
    const ogImg = doctorImagePath
      ? getAbsoluteImageUrl(doctorImagePath)
      : `${window.location.origin}/default-doctor-og-image.jpg`; // fallback image

    // const title = `Book Appointment with Dr. ${doctorName} - ${
    //   specialties || "Specialist"
    // } | Deedmed`;

    // const description = `Book online appointment with Dr. ${doctorName}${
    //   qualifications ? `, ${qualifications}` : ""
    // }${experience ? ` with ${experience}+ years experience` : ""}. ${
    //   specialties ? `Specializes in ${specialties}. ` : ""
    // }Consultation fee: LKR ${fee}. Book now for telemedicine consultation in Sri Lanka.`;

    // const keywords = [
    //   `Dr ${doctorName}`,
    //   "online doctor appointment",
    //   "telemedicine Sri Lanka",
    //   "doctor consultation",
    //   specialties,
    //   "SLMC registered doctor",
    //   "video consultation",
    //   "medical appointment booking",
    // ]
    //   .filter(Boolean)
    //   .join(", ");

    const title = `Book Appointment with ${displayName} - ${
      specialties || professionLabel
    } | Deedmed`;

    const description = `Book an online appointment with ${displayName}${
      qualifications ? `, ${qualifications}` : ""
    }${experience ? ` with ${experience}+ years experience` : ""}. ${
      specialties ? `Specialises in ${specialties}. ` : ""
    }Consultation fee: LKR ${fee}. Book now for a telemedicine consultation in Sri Lanka.`;

    const keywords = [
      displayName,
      `online ${professionLabel.toLowerCase()} appointment`,
      "telemedicine Sri Lanka",
      specialties,
      // Only claim SLMC registration for professions that actually hold it.
      isDoctor ? "SLMC registered doctor" : null,
      "video consultation",
      "appointment booking",
    ]
      .filter(Boolean)
      .join(", ");

    // Structured Data for Doctor/Medical Professional
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "MedicalBusiness",
      name: displayName,
      description:
        doctorData?.profileDescription ||
        `Experienced ${
          specialties || professionLabel.toLowerCase()
        } providing online consultations`,
      url: `https://deedmed.lk/appointments/${path}`,
      // telephone: "+94-xxx-xxx-xxxx", // Add your contact number
      address: {
        "@type": "PostalAddress",
        addressCountry: doctorData?.User?.country || "LK",
        addressRegion:
          doctorData?.User?.state ||
          doctorData?.User?.province ||
          "Western Province",
        addressLocality: doctorData?.User?.city || "Colombo",
      },
      priceRange: `LKR ${fee}`,
      paymentAccepted: ["Cash", "Credit Card", "Online Payment"],
      currenciesAccepted: "LKR",
      openingHours: "Mo-Su 08:00-20:00", // Adjust based on actual hours
      hasOfferCatalog: {
        "@type": "OfferCatalog",
        name: "Medical Consultation Services",
        itemListElement: [
          {
            "@type": "Offer",
            itemOffered: {
              "@type": "MedicalProcedure",
              name: "Online Consultation",
              description: `Professional consultation with ${displayName}`,
            },
            price: fee,
            priceCurrency: "LKR",
          },
        ],
      },
      medicalSpecialty: doctorData?.serviceCategories || [],
      availableService: {
        "@type": "MedicalService",
        name: "Telemedicine Consultation",
        // schema.org Physician is a specific claim. Only make it for doctors;
        // everyone else is a Person with a stated job title.
        provider: isDoctor
          ? {
              "@type": "Physician",
              name: displayName,
              medicalSpecialty: specialties,
            }
          : {
              "@type": "Person",
              name: displayName,
              jobTitle: professionLabel,
            },
      },
    };

    // Additional structured data for appointment booking
    const appointmentSchema = {
      "@context": "https://schema.org",
      "@type": "Service",
      serviceType: "Medical Appointment Booking",
      provider: {
        "@type": "MedicalOrganization",
        name: "Deedmed",
        url: "https://deedmed.lk",
      },
      areaServed: {
        "@type": "Country",
        name: "Sri Lanka",
      },
      availableChannel: {
        "@type": "ServiceChannel",
        serviceType: "Online Booking",
        availableLanguage: ["en", "si"],
      },
    };

    return {
      title,
      description,
      keywords,
      structuredData: [structuredData, appointmentSchema],
      canonicalUrl: `https://deedmed.lk/appointments/${path}`,
      ogImg, // Fix: Include ogImg in the returned object
    };
  };

  const seoData = generateSEOData();

  // Debug logging (remove in production)
  // console.log("Doctor profile image path:", doctorData?.User?.profileImagePath);
  // console.log("SEO data ogImg:", seoData.ogImg);

  // if (loading || !doctorData) {
  if (!doctorData || initialLoading) {
    return (
      <>
        <SEOHelmet
          title="Loading Doctor Details - Deedmed"
          description="Please wait while we load the doctor's information for appointment booking."
          keywords="doctor appointment, telemedicine, Sri Lanka"
        />
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="70vh"
        >
          <CircularProgress size={50} color="primary" />
        </Box>
      </>
    );
  }

  return (
    <>
      <SEOHelmet
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        structuredData={seoData.structuredData}
        canonicalUrl={seoData.canonicalUrl}
        ogTitle={`Book Appointment with ${displayName}`}
        ogDescription={`Professional consultation with ${displayName}. Book your appointment now!`}
        ogImage={seoData.ogImg} // Fix: Use seoData.ogImg correctly
        twitterCard="summary_large_image"
      />

      <PageWrapper>
        <ContentWrapper>
          {/* Header */}
          <Stack direction="row" alignItems="center" spacing={2} mb={3}>
            <Button
              onClick={() => navigate(-1)}
              startIcon={
                <ArrowCircleLeftOutlinedIcon
                  sx={{ fontSize: 30, color: "#1976d2" }}
                />
              }
              aria-label="Go back to previous page"
            >
              Back
            </Button>
          </Stack>

          {/* Doctor Info */}
          <ProfileCard>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={7}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar
                    src={doctorData?.User?.profileImagePath}
                    alt={`${displayName} profile picture`}
                    sx={{ width: 90, height: 90, border: "2px solid white" }}
                  />
                  <Box>
                    <Typography
                      variant="h1"
                      component="h1"
                      fontSize="2rem"
                      fontWeight={700}
                    >
                      {displayName}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      {professionLabel}
                      {doctorData?.qualifications?.length
                        ? ` · ${doctorData.qualifications.join(", ")}`
                        : ""}
                      {/* Only show a registration when there is one. A counsellor
                          has no council, and "SLMC undefined" reads as broken. */}
                      {doctorData?.regNo
                        ? ` · ${doctorData.regNoAuthority || "Reg."} ${doctorData.regNo}`
                        : ""}
                    </Typography>
                    {/* No rating until there's a real one. A permanent 5.0 on
                        every provider is worse than showing nothing: it can't
                        differentiate, and it misleads. Review count is honest. */}
                    {doctorData?.DoctorAnalytic?.reviewCount > 0 && (
                      <Typography variant="caption" sx={{ opacity: 0.9 }}>
                        {doctorData.DoctorAnalytic.reviewCount} review
                        {doctorData.DoctorAnalytic.reviewCount === 1 ? "" : "s"}
                      </Typography>
                    )}
                    {doctorData?.experienceYears && (
                      <Typography
                        variant="caption"
                        style={{
                          borderRadius: 25,
                          background: "rgba(197, 227, 255, 1)",
                          padding: "2px 6px",
                          marginLeft: 8,
                          color: "#1e40af",
                        }}
                      >
                        {doctorData?.experienceYears}+ Years Experience
                      </Typography>
                    )}
                  </Box>
                </Stack>

                {doctorData?.serviceCategories && (
                  <Box
                    mt={2}
                    display="flex"
                    flexWrap="wrap"
                    alignItems="center"
                  >
                    {doctorData.serviceCategories.map((cat) => (
                      <Chip
                        key={cat}
                        label={cat}
                        size="small"
                        sx={{
                          mr: 1,
                          mb: 1,
                          background: "rgba(255,255,255,0.2)",
                          color: "white",
                        }}
                      />
                    ))}
                  </Box>
                )}
              </Grid>
              <Grid
                item
                xs={12}
                md={5}
                textAlign={{ xs: "left", md: "right" }}
                style={{ display: "flex", flexDirection: "column" }}
              >
                <FeeTag>LKR {doctorData?.displayingFee}</FeeTag>
                <Typography variant="caption">Per Consultation</Typography>
              </Grid>
            </Grid>

            {/* <Box mt={3}>
              <Typography variant="body2" mt={1} sx={{ opacity: 0.8 }}>
                Their time zone: {doctorData?.User?.timezone}
              </Typography>
            </Box> */}
          </ProfileCard>

          {/* About Doctor */}
          <SectionCard>
            <Typography
              variant="h2"
              component="h2"
              fontSize="1.5rem"
              fontWeight={600}
              gutterBottom
            >
              {/* About Dr. {doctorData?.name} */}
              About {displayName}
            </Typography>
            {doctorData?.profileDescription && (
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {doctorData?.profileDescription}
              </Typography>
            )}
          </SectionCard>

          {/* Time Zone */}
          <SectionCard>
            <Grid
              container
              display={"flex"}
              justifyContent={"center"}
              alignItems={"center"}
            >
              <Grid item xs={12} md={2}>
                <Typography variant="subtitle2" sx={{ opacity: 0.8, mb: 1 }}>
                  Your Time Zone
                </Typography>
              </Grid>
              <Grid item xs={12} md={10}>
                <TimezoneSelect
                  value={selectedUserTimeZone}
                  onChange={handleUsersTimeZone}
                  aria-label="Select your timezone"
                />
              </Grid>
            </Grid>
          </SectionCard>

          {/* Booking */}
          <SectionCard>
            <Typography
              variant="h2"
              component="h2"
              fontSize="1.5rem"
              fontWeight={600}
              mb={2}
            >
              Select Appointment Date & Time
            </Typography>

            {/* Appointment Time Selection Module */}
            <TimePickerModule
              doctorData={doctorData}
              onBook={(slot) => handleBooking(slot)}
              userTimeZone={selectedUserTimeZone}
              fetchDoctorDetailsByDate={fetchDoctorDetailsByDate}
              dateLoading={dateLoading} // pass dateLoading
              // NEW: Pass the handler to receive the selected slot data
              onSlotSelect={handleSlotSelection}
              // selectedAptData={aptSelectedDate}
            />
          </SectionCard>

          {/* Booking Stepper Model */}
          <BookingModel
            openModal={bookingModalOpen}
            closeModal={() => {
              setBookingModalOpen(false);
              // window.location.reload();
            }}
            selectedAptData={selectedAppointmentDetails}
            doctorDetails={doctorData}
            userTimeZone={selectedUserTimeZone}
            userDetails={userInfo}
            onAuthenticationSuccess={handleAuthenticationSuccess}
          />

          {/* <Typography >context</Typography>
    <Typography>User Data : {JSON.stringify(userInfo)}</Typography>
 <Typography>User : ID = {JSON.stringify(userInfo.userId)} , Type = {userInfo.userType} Timezone = {}</Typography> 
 <Typography>Doctor : Doctor ID = {JSON.stringify(doctorData.doctorId)}, Doctor Name = {doctorData.name } , Charge = {doctorData.displayingFee}</Typography>
 <Typography>Appointment : Date = {JSON.stringify(selectedAppointmentDetails)} Time = {} UTC = {} </Typography> */}
        </ContentWrapper>
        <ScrollToTop threshold={300} color="#0090e8" size="small" />
      </PageWrapper>
    </>
  );
};

export default AddAppointments;
